/**
 * InsuranceVerificationPage (Phase-2, Module 3 — admin/coordinator side)
 *
 * Lists officer insurance policies (filterable by status), verifies pending
 * ones (approve / reject with reason), and surfaces near-expiry policies.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Shield as ShieldIcon, Verified as VerifyIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, ConfirmDialog } from '../../components/common';
import { InsuranceApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import type { OfficerInsurance } from '../../types/phase2';

const InsuranceVerificationPage: React.FC = () => {
  const [rows, setRows] = useState<OfficerInsurance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('PENDING_VERIFICATION');
  const [rejectId, setRejectId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: Record<string, unknown> = { page: 0, size: 100 };
      if (statusFilter) params.status = statusFilter;
      const page = await InsuranceApi.listAll(params);
      setRows(page?.content || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load insurance'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const doVerify = async (id: number, approved: boolean, reason?: string) => {
    try { await InsuranceApi.verify(id, approved, reason); setRejectId(null); load(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Verify failed'); }
  };

  const columns: GridColDef[] = [
    { field: 'officerName', headerName: 'Officer', width: 170, renderCell: (p) => p.value || '—' },
    { field: 'insuranceCompanyName', headerName: 'Company', flex: 1.3, minWidth: 160 },
    { field: 'policyNumber', headerName: 'Policy No.', width: 150 },
    { field: 'policyEndDate', headerName: 'Expiry', width: 120 },
    { field: 'coverageAmount', headerName: 'Coverage', width: 120, renderCell: (p) => p.value ? `₹${Number(p.value).toLocaleString()}` : '—' },
    { field: 'status', headerName: 'Status', width: 160, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Action', width: 220, sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" variant="contained" color="success" startIcon={<VerifyIcon />}
            disabled={p.row.status !== 'PENDING_VERIFICATION'}
            onClick={() => doVerify(p.row.id, true)} sx={{ textTransform: 'none' }}>Approve</Button>
          <Button size="small" variant="outlined" color="error"
            disabled={p.row.status !== 'PENDING_VERIFICATION'}
            onClick={() => setRejectId(p.row.id)} sx={{ textTransform: 'none' }}>Reject</Button>
        </Box>
      ),
    },
  ];

  return (
    <PageScaffold title="Insurance Verification" subtitle="Verify pending officer insurance policies" icon={<ShieldIcon />}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="PENDING_VERIFICATION">Pending Verification</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="REJECTED">Rejected</MenuItem>
          <MenuItem value="EXPIRED">Expired</MenuItem>
        </Select>
      </FormControl>
      <Box sx={{ height: 520, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>
      <ConfirmDialog open={rejectId != null} title="Reject Insurance" message="Provide a reason for rejecting this insurance policy."
        confirmLabel="Reject" requireReason reasonLabel="Rejection reason"
        onConfirm={(reason) => { if (rejectId != null) doVerify(rejectId, false, reason); }} onClose={() => setRejectId(null)} />
    </PageScaffold>
  );
};

export default InsuranceVerificationPage;